/**
 * Build the `download` attribute / suggested filename for a CallRecording.
 *
 * OSPROD-54 spec: `RoomName_YYYY-MM-DD_HH-MM-SS.ext`.
 *
 * When the channel sits inside a server / space we keep that as a hyphen-
 * joined prefix on the room name (`Server-Room_…`) so identically-named
 * rooms across different servers don't collide in the user's downloads
 * folder. Falls back to `recording-<id>` only when no name context is
 * available (orphaned legacy rows).
 *
 * Hoisted out of RecordingsList.tsx so the standalone Recordings page
 * (RecordingItem.tsx → /recordings/:channelId) and the in-call dropdown
 * produce identical filenames — previously the page used a bare
 * `<a download>` and saved files as `<recordingId>.mp4`.
 */

/** Structural type — both CallRecording (store) and RecordingRow (page) satisfy it. */
export interface RecordingForFilename {
  id: string
  startedAt: string
  format: string
  channel?: {
    name: string
    server?: { name: string } | null
    space?: { name: string } | null
  } | null
}

/**
 * Filenames travel through HTTP headers (Content-Disposition) and OS file
 * systems, so we keep them slash-free. Cyrillic / other scripts are
 * preserved when possible (modern OSes handle them) but the problem
 * characters are stripped rather than URL-encoded so the user sees a
 * clean name in their downloads folder.
 */
export function slugifyForFilename(s: string): string {
  return s
    .replace(/[\\/:*?"<>|]/g, '') // forbidden on Windows
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
}

export function buildDownloadName(rec: RecordingForFilename): string {
  const parent = rec.channel?.server?.name ?? rec.channel?.space?.name ?? ''
  const channel = rec.channel?.name ?? ''
  // Local time, not UTC — the user looking at the file expects "when did
  // I record it" in their own clock.
  const d = new Date(rec.startedAt)
  const pad = (n: number) => String(n).padStart(2, '0')
  const stamp =
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`
  const nameParts = [parent, channel].map(slugifyForFilename).filter(Boolean)
  const roomName = nameParts.length > 0
    ? nameParts.join('-')
    : `recording-${rec.id.slice(0, 8)}`
  const ext = rec.format || 'mp4'
  // Underscore separator between room name and timestamp matches OSPROD-54.
  return `${roomName}_${stamp}.${ext}`
}
